import '@aws-amplify/ui-react/styles.css';
import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Button, Form, Header, Modal } from 'semantic-ui-react';

import { fetchUserAttributes } from '@aws-amplify/auth';
import { resetPassword } from 'aws-amplify/auth';
import 'react-datepicker/dist/react-datepicker.css';
import {
  getCurrentAccount,
  getCurrentOrganizationMember,
} from '../../../../../common/selectors/auth';

import { Footer } from 'react-day-picker';
import styled from 'styled-components';
import { PageContent } from '../../../../../common/styles/page';
import { useUpdateUserPreferences } from '../api';
import ConfirmPasswordReset from './ConfirmPasswordReset';
import Input from './Input';
import { useToasts } from 'common/toast';

const MemberSettings = () => {
  const account = useSelector(getCurrentAccount);
  const member = useSelector(getCurrentOrganizationMember);
  const { updateUserPreferences, isLoading, isError } = useUpdateUserPreferences();

  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);
  const [isMfaModalOpen, setIsMfaModalOpen] = useState(false);
  const [username, setUsername] = useState('');
  const { addToast } = useToasts();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(
      () => ({
        firstName: account?.firstName || '',
        lastName: account?.lastName || '',
        email: account?.email || '',
        picture: account?.picture || '',
      }),
      [account]
    ),
  });

  const onSubmit = async (data: any) => {
    if (member && account) {
      try {
        const preferences = {
          first_name: data.firstName,
          last_name: data.lastName,
        };
        await updateUserPreferences(account.id, preferences);
        addToast('Your profile has been updated successfully!', { appearance: 'success' });
      } catch (err) {
        console.error('Error updating user preferences:', err);
        addToast('There was an error updating your profile. Please try again.', {
          appearance: 'error',
        });
      }
    }
  };

  const handlePasswordResetClick = async () => {
    try {
      const userAttributes = await fetchUserAttributes();
      // Use email as the Amplify username for resetPassword. This works in both
      // pool configs: old pool (AliasAttributes:["email"]) resolves email through
      // the alias; new pool (UsernameAttributes:["email"]) has email as the
      // Username directly. preferred_username is unreliable in the old pool and
      // not auto-populated in the new pool.
      const email = userAttributes['email'];
      if (!email) {
        console.error('No email found in user attributes');
        return;
      }
      setUsername(email);
      await resetPassword({ username: email });
      setIsMfaModalOpen(true);
    } catch (error) {
      console.error('Error initiating password reset:', error);
    }
  };

  const handleMfaSuccess = () => {
    setIsMfaModalOpen(false);
    setIsPasswordResetModalOpen(true);
    // TODO: We can force log people out here. We probably should?
  };

  return (
    <PageContent>
      <Heading>Update Account Profile</Heading>
      <FormContainer>
        <Form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
          <FormSection>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: 'First name is required' }}
              render={({ field }) => (
                <Input
                  placeholder="First Name"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.firstName?.message?.toString()}
                />
              )}
            />

            <Controller
              name="lastName"
              control={control}
              rules={{ required: 'Last name is required' }}
              render={({ field }) => (
                <Input
                  placeholder="Last Name"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.lastName?.message?.toString()}
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              }}
              render={({ field }) => (
                <Input
                  placeholder="Email"
                  type="email"
                  disabled={true}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.email?.message?.toString()}
                  style={{
                    color: '#4a4a4a',
                    backgroundColor: '#f5f5f5',
                    opacity: 1,
                  }}
                />
              )}
            />

            <Footer
              style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Button
                type="button"
                onClick={handlePasswordResetClick}
                disabled={isLoading}
                color="blue"
              >
                Reset Password
              </Button>
              <Button type="submit" loading={isLoading} disabled={isLoading} color="green">
                Save Changes
              </Button>
            </Footer>
          </FormSection>
        </Form>
      </FormContainer>

      <ConfirmPasswordReset
        isOpen={isMfaModalOpen}
        onClose={() => setIsMfaModalOpen(false)}
        onSubmit={handleMfaSuccess}
        username={username}
      />

      <Modal
        open={isPasswordResetModalOpen}
        onClose={() => setIsPasswordResetModalOpen(false)}
        size="small"
      >
        <Modal.Header>Reset Password</Modal.Header>
        <Modal.Content>
          <div>
            <Header as="h3">Password Reset Complete</Header>
            <p>Your password has been reset successfully.</p>
            <Button onClick={() => setIsPasswordResetModalOpen(false)}>Close</Button>
          </div>
        </Modal.Content>
      </Modal>
    </PageContent>
  );
};

const Heading = styled.div`
  font-size: 22px;
  font-weight: bolder;
  font-family: 'Red Hat Display', sans-serif;
  margin-top: 30px;
  margin-bottom: 10px;
  text-align: center;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0 0px 200px 0px;
    width: 100%;
  }
`;

const FormSection = styled.div`
  width: 100%;
  margin-right: 30px;
`;

export default MemberSettings;
